<template>
  <div class="negative-keyword-table">
    <div class="flex items-center gap-2 mb-3">
      <input v-model="filterText" type="text" placeholder="Filter keywords..."
        class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500">
      <select v-model="filterMatchType"
        v-tooltip="'Filter rows by match type. Hover the column header for explanations.'"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm">
        <option value="">
          All match types
        </option>
        <option value="broad">
          Broad
        </option>
        <option value="phrase">
          Phrase
        </option>
        <option value="exact">
          Exact
        </option>
      </select>
      <select v-model="filterSource"
        v-tooltip="'Filter rows by where the keyword came from.'"
        class="px-3 py-2 border border-gray-300 rounded-lg text-sm">
        <option value="">
          All sources
        </option>
        <option value="manual">
          Manual
        </option>
        <option value="library_import">
          Library import
        </option>
        <option value="scan">
          Scan
        </option>
      </select>
    </div>

    <div v-if="selectedIds.size > 0" class="flex items-center gap-2 mb-3 p-3 bg-primary-50 rounded-lg">
      <span class="text-sm text-primary-900">{{ selectedIds.size }} selected</span>
      <button v-tooltip="MATCH_TYPE_TOOLTIPS.broad"
        @click="bulkChangeMatchType('broad')" class="px-2 py-1 text-xs bg-white border rounded">
        Set Broad
      </button>
      <button v-tooltip="MATCH_TYPE_TOOLTIPS.phrase"
        @click="bulkChangeMatchType('phrase')" class="px-2 py-1 text-xs bg-white border rounded">
        Set Phrase
      </button>
      <button v-tooltip="MATCH_TYPE_TOOLTIPS.exact"
        @click="bulkChangeMatchType('exact')" class="px-2 py-1 text-xs bg-white border rounded">
        Set Exact
      </button>
      <button v-tooltip="'Permanently delete the selected keywords. Source attribution will be lost.'"
        @click="bulkDelete" class="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded">
        Delete
      </button>
    </div>

    <table class="min-w-full divide-y divide-gray-200 bg-white rounded-xl shadow-sm overflow-hidden">
      <thead class="bg-gray-50">
        <tr>
          <th class="w-10 px-3 py-2">
            <input type="checkbox" :checked="allFilteredSelected" @change="toggleAll">
          </th>
          <th @click="sortBy('keyword')" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">
            Keyword
          </th>
          <th @click="sortBy('match_type')" class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">
            <span>Match Type</span>
            <i v-tooltip="MATCH_TYPE_HEADER_TOOLTIP"
              class="far fa-circle-info text-primary-300 ml-1" />
          </th>
          <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            <span>Source</span>
            <i v-tooltip="SOURCE_HEADER_TOOLTIP"
              class="far fa-circle-info text-primary-300 ml-1" />
          </th>
          <th class="px-3 py-2 w-10" />
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <tr v-if="filteredSorted.length === 0">
          <td colspan="5" class="px-3 py-6 text-center text-sm text-gray-500">
            <span v-if="rows.length === 0">No keywords yet — add one below or use Paste multiple to import a batch.</span>
            <span v-else>No keywords match your filters.</span>
          </td>
        </tr>
        <tr v-for="row in filteredSorted" :key="row.id || row._tempId" class="hover:bg-gray-50">
          <td class="px-3 py-2">
            <input type="checkbox" :checked="selectedIds.has(rowKey(row))" @change="toggleSelect(row)">
          </td>
          <td class="px-3 py-2">
            <span
              contenteditable="plaintext-only"
              role="textbox"
              aria-label="Edit keyword"
              tabindex="0"
              @blur="onKeywordBlur(row, $event)"
              @keydown.enter.prevent="$event.target.blur()"
              class="block w-full px-2 py-1 border border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none rounded text-sm cursor-text">{{ row.keyword }}</span>
          </td>
          <td class="px-3 py-2">
            <select v-model="row.match_type" @change="commitRow(row)"
              v-tooltip="MATCH_TYPE_TOOLTIPS[row.match_type] || ''"
              class="px-2 py-1 border border-gray-300 rounded text-sm">
              <option value="broad">
                Broad
              </option>
              <option value="phrase">
                Phrase
              </option>
              <option value="exact">
                Exact
              </option>
            </select>
          </td>
          <td class="px-3 py-2">
            <span :class="sourceBadgeClass(row.source)"
              v-tooltip="SOURCE_TOOLTIPS[row.source] || ''"
              class="px-2 py-1 text-xs rounded-full">{{ sourceLabel(row.source) }}</span>
          </td>
          <td class="px-3 py-2 text-right">
            <button v-if="row.id" @click="deleteRow(row)"
              v-tooltip="'Delete this keyword'"
              class="text-red-500 hover:text-red-700 text-sm">
              ×
            </button>
          </td>
        </tr>
        <tr>
          <td />
          <td class="px-3 py-2">
            <input v-model="newKeyword" @keyup.enter="addNew" placeholder="Add a keyword..."
              class="w-full px-2 py-1 border border-gray-300 rounded text-sm">
          </td>
          <td class="px-3 py-2">
            <select v-model="newMatchType"
              v-tooltip="MATCH_TYPE_TOOLTIPS[newMatchType] || ''"
              class="px-2 py-1 border border-gray-300 rounded text-sm">
              <option value="broad">
                Broad
              </option>
              <option value="phrase">
                Phrase
              </option>
              <option value="exact">
                Exact
              </option>
            </select>
          </td>
          <td colspan="2" class="px-3 py-2 text-right">
            <button @click="addNew" class="px-3 py-1 bg-cta-500 text-white text-sm rounded">
              Add
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <details class="mt-4">
      <summary class="text-sm text-gray-600 cursor-pointer">
        Paste multiple (one per line)
      </summary>
      <p class="text-xs text-gray-500 mt-2">
        Paste a batch of keywords (one per line). Each will be added with the match type selected below.
      </p>
      <textarea v-model="pasteText" rows="6" placeholder="Paste keywords, one per line"
        class="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
      <div class="flex items-center gap-2 mt-2">
        <select v-model="pasteMatchType"
          v-tooltip="MATCH_TYPE_TOOLTIPS[pasteMatchType] || ''"
          class="px-2 py-1 border border-gray-300 rounded text-sm">
          <option value="broad">
            Broad
          </option>
          <option value="phrase">
            Phrase
          </option>
          <option value="exact">
            Exact
          </option>
        </select>
        <button @click="commitPaste" class="px-3 py-1 bg-cta-500 text-white text-sm rounded">
          Add All
        </button>
      </div>
    </details>

    <p v-if="error" class="mt-2 text-sm text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script>
import vTooltip from '~/javascript/directives/tooltip'
import { toastSuccess, toastError } from '~/javascript/utils/toast'
import { confirmAlert } from '~/javascript/utils/confirm_alert'

const MATCH_TYPE_TOOLTIPS = {
  broad: "Blocks any query containing all the keyword's words. Most flexible / least precise.",
  phrase: 'Blocks queries containing the keyword as an exact phrase. Balanced.',
  exact: 'Blocks only this exact query. Most precise.'
}

const MATCH_TYPE_HEADER_TOOLTIP = '<b>Broad</b>: matches any order of words.<br><b>Phrase</b>: matches the words in order.<br><b>Exact</b>: matches only the exact query.'

const SOURCE_TOOLTIPS = {
  manual: 'Typed by a user.',
  library_import: 'Imported from an applied list (admin or shared library).',
  scan: 'Discovered by a competitor scan and approved.'
}

const SOURCE_HEADER_TOOLTIP = 'Where the keyword originated.<br><b>Manual</b>: typed.<br><b>Library</b>: imported.<br><b>Scan</b>: from a competitor scan.'

export default {
  name: 'KeywordTable',
  directives: { tooltip: vTooltip },
  props: {
    initialKeywords: { type: Array, required: true },
    upsertUrl: { type: String, required: true },
    deleteUrlBase: { type: String, required: true },
    csrfToken: { type: String, required: true },
    defaultMatchType: { type: String, default: 'broad' }
  },
  data() {
    return {
      rows: this.initialKeywords.map((k) => ({ ...k })),
      newKeyword: '',
      newMatchType: this.defaultMatchType,
      pasteText: '',
      pasteMatchType: this.defaultMatchType,
      filterText: '',
      filterMatchType: '',
      filterSource: '',
      sortKey: 'keyword',
      sortDir: 'asc',
      selectedIds: new Set(),
      error: null,
      MATCH_TYPE_TOOLTIPS,
      MATCH_TYPE_HEADER_TOOLTIP,
      SOURCE_TOOLTIPS,
      SOURCE_HEADER_TOOLTIP
    }
  },
  computed: {
    filteredSorted() {
      let out = this.rows
      if (this.filterText) out = out.filter((r) => r.keyword.toLowerCase().includes(this.filterText.toLowerCase()))
      if (this.filterMatchType) out = out.filter((r) => r.match_type === this.filterMatchType)
      if (this.filterSource) out = out.filter((r) => r.source === this.filterSource)
      const dir = this.sortDir === 'asc' ? 1 : -1
      return [...out].sort((a, b) => (a[this.sortKey] > b[this.sortKey] ? 1 : -1) * dir)
    },
    allFilteredSelected() {
      return this.filteredSorted.length > 0 && this.filteredSorted.every((r) => this.selectedIds.has(this.rowKey(r)))
    }
  },
  methods: {
    // eslint-disable-next-line no-underscore-dangle
    rowKey(row) { return row.id || row._tempId },
    sortBy(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      else { this.sortKey = key; this.sortDir = 'asc' }
    },
    toggleSelect(row) {
      const k = this.rowKey(row)
      if (this.selectedIds.has(k)) this.selectedIds.delete(k); else this.selectedIds.add(k)
      this.selectedIds = new Set(this.selectedIds)
    },
    toggleAll() {
      if (this.allFilteredSelected) {
        this.filteredSorted.forEach((r) => this.selectedIds.delete(this.rowKey(r)))
      } else {
        this.filteredSorted.forEach((r) => this.selectedIds.add(this.rowKey(r)))
      }
      this.selectedIds = new Set(this.selectedIds)
    },
    onKeywordBlur(row, event) {
      const text = (event.target.textContent || '').trim()
      if (text === row.keyword) return
      row.keyword = text
      this.commitRow(row)
    },
    async commitRow(row) {
      this.error = null
      const payload = { rows: [{ id: row.id, keyword: row.keyword, match_type: row.match_type }]}
      try {
        const data = await this.postJson(this.upsertUrl, payload)
        const saved = data.keywords?.[0]
        if (saved) Object.assign(row, saved)
      } catch (e) { this.error = e.message; toastError(e) }
    },
    async addNew() {
      if (!this.newKeyword.trim()) return
      this.error = null
      const payload = { rows: [{ keyword: this.newKeyword.trim(), match_type: this.newMatchType }]}
      try {
        const data = await this.postJson(this.upsertUrl, payload)
        if (data.keywords?.[0]) this.rows.push(data.keywords[0])
        toastSuccess(`Added "${this.newKeyword.trim()}"`)
        this.newKeyword = ''
      } catch (e) { this.error = e.message; toastError(e) }
    },
    async commitPaste() {
      const lines = this.pasteText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
      if (lines.length === 0) return
      const rows = lines.map((kw) => ({ keyword: kw, match_type: this.pasteMatchType }))
      try {
        const data = await this.postJson(this.upsertUrl, { rows })
        const added = data.keywords || []
        this.rows.push(...added)
        toastSuccess(`Added ${added.length} keyword${added.length === 1 ? '' : 's'}`)
        this.pasteText = ''
      } catch (e) { this.error = e.message; toastError(e) }
    },
    async deleteRow(row) {
      if (!row.id) return
      try {
        await this.deleteRequest(`${this.deleteUrlBase}/${row.id}`)
        this.rows = this.rows.filter((r) => r.id !== row.id)
        this.selectedIds.delete(row.id); this.selectedIds = new Set(this.selectedIds)
        toastSuccess('Keyword removed')
      } catch (e) { this.error = e.message; toastError(e) }
    },
    async bulkChangeMatchType(mt) {
      const ids = [...this.selectedIds]
      const payload = { rows: ids.map((id) => {
        const row = this.rows.find((r) => r.id === id)
        return { id, keyword: row.keyword, match_type: mt }
      }) }
      try {
        const data = await this.postJson(this.upsertUrl, payload);
        (data.keywords || []).forEach((saved) => {
          const r = this.rows.find((x) => x.id === saved.id); if (r) Object.assign(r, saved)
        })
        toastSuccess(`Set match type to ${mt} for ${ids.length} keyword${ids.length === 1 ? '' : 's'}`)
      } catch (e) { this.error = e.message; toastError(e) }
    },
    async bulkDelete() {
      const count = this.selectedIds.size
      const ok = await confirmAlert({
        title: `Delete ${count} keyword${count === 1 ? '' : 's'}?`,
        text: 'This is permanent. Source attribution (Manual / Library / Scan) will be lost — re-importing will recreate the rows as Manual.',
        confirmButtonText: 'Yes, delete'
      })
      if (!ok) return
      const ids = [...this.selectedIds]
      const settled = await Promise.allSettled(ids.map((id) => this.deleteRequest(`${this.deleteUrlBase}/${id}`)))
      const lastError = settled.find((s) => s.status === 'rejected')
      if (lastError) { this.error = lastError.reason.message; toastError(lastError.reason) }
      this.rows = this.rows.filter((r) => !this.selectedIds.has(r.id))
      this.selectedIds = new Set()
      if (!lastError) toastSuccess(`Deleted ${ids.length} keyword${ids.length === 1 ? '' : 's'}`)
    },
    sourceBadgeClass(src) {
      return {
        manual: 'bg-gray-100 text-gray-700',
        library_import: 'bg-primary-100 text-primary-700',
        scan: 'bg-amber-100 text-amber-700'
      }[src] || 'bg-gray-100 text-gray-700'
    },
    sourceLabel(src) { return ({ manual: 'Manual', library_import: 'Library', scan: 'Scan' })[src] || src },
    async postJson(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const message = (data.errors && data.errors.length) ? data.errors.join(', ') : `Save failed: ${res.status}`
        throw new Error(message)
      }
      return res.json()
    },
    async deleteRequest(url) {
      const res = await fetch(url, { method: 'DELETE', headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' } })
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`)
    }
  }
}
</script>
